import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined, InfoCircleOutlined, LoadingOutlined, DownOutlined } from '@ant-design/icons';
import { ordersService } from '../../services';
import { ProductCategory } from '../../constants/productCategory';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import yaml from 'js-yaml';
import { useMarketplacePricing } from '../../hooks/useMarketplacePricing';
import MarketplacePricingSelector from '../../components/MarketplacePricingSelector';

// ─── Types ───────────────────────────────────────────────
interface DockerHostConfig {
  name: string;
  publicAccess: boolean;
  composeYaml: string;
  hostPort: number;
  healthPath: string;
}

// ─── Constants ───────────────────────────────────────────
const COMPOSE_EXAMPLES: Record<string, { label: string; yaml: string }> = {
  nginx: {
    label: 'Nginx Hello World',
    yaml: `services:
  web:
    image: nginx:alpine
    ports:
      - "80:80"
    restart: unless-stopped`,
  },
  wordpress: {
    label: 'WordPress + MySQL',
    yaml: `services:
  wordpress:
    image: wordpress:latest
    ports:
      - "80:80"
    environment:
      WORDPRESS_DB_HOST: db
      WORDPRESS_DB_USER: wp
      WORDPRESS_DB_PASSWORD: changeme
      WORDPRESS_DB_NAME: wordpress
    restart: unless-stopped
    depends_on:
      - db

  db:
    image: mysql:8
    environment:
      MYSQL_DATABASE: wordpress
      MYSQL_USER: wp
      MYSQL_PASSWORD: changeme
      MYSQL_ROOT_PASSWORD: rootchangeme
    volumes:
      - db_data:/var/lib/mysql
    restart: unless-stopped

volumes:
  db_data:`,
  },
  node_postgres: {
    label: 'Node.js API + PostgreSQL',
    yaml: `services:
  api:
    image: node:20-alpine
    ports:
      - "3000:3000"
    environment:
      DATABASE_URL: postgres://app:changeme@db:5432/appdb
    command: node server.js
    restart: unless-stopped
    depends_on:
      - db

  db:
    image: postgres:16-alpine
    environment:
      POSTGRES_DB: appdb
      POSTGRES_USER: app
      POSTGRES_PASSWORD: changeme
    volumes:
      - pgdata:/var/lib/postgresql/data
    restart: unless-stopped

volumes:
  pgdata:`,
  },
  redis_commander: {
    label: 'Redis + Commander',
    yaml: `services:
  redis:
    image: redis:7-alpine
    ports:
      - "6379:6379"
    restart: unless-stopped

  commander:
    image: rediscommander/redis-commander:latest
    ports:
      - "8081:8081"
    environment:
      REDIS_HOSTS: local:redis:6379
    restart: unless-stopped
    depends_on:
      - redis`,
  },
};

// ─── YAML Validation ────────────────────────────────────
const validateYaml = (text: string): { valid: boolean; error?: string } => {
  if (!text.trim()) return { valid: false, error: 'Compose YAML is required' };
  try {
    const parsed = yaml.load(text);
    if (!parsed || typeof parsed !== 'object') return { valid: false, error: 'YAML must be an object' };
    const services = (parsed as any).services;
    if (!services || typeof services !== 'object' || Object.keys(services).length === 0) {
      return { valid: false, error: 'Must define at least one service under "services:"' };
    }
    for (const [name, cfg] of Object.entries(services)) {
      if (!(cfg as any).image) return { valid: false, error: `Service "${name}" must have an "image"` };
    }
    return { valid: true };
  } catch (e: any) {
    return { valid: false, error: e.message };
  }
};

// ─── Component ───────────────────────────────────────────
const CreateDockerHost: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { products: allProducts, getProductsByCategory } = useProducts();
  const { success, error: showError } = useNotificationContext();

  const [step, setStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [yamlError, setYamlError] = useState<string | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const pricing = useMarketplacePricing();

  const [config, setConfig] = useState<DockerHostConfig>({
    name: '',
    publicAccess: true,
    composeYaml: '',
    hostPort: 80,
    healthPath: '/',
  });

  const products = allProducts[ProductCategory.DOCKER] || [];

  useEffect(() => {
    if (accessToken && products.length === 0) {
      getProductsByCategory(ProductCategory.DOCKER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ─── Debounced YAML Validation ─────────────────────────
  useEffect(() => {
    if (!config.composeYaml.trim()) {
      setYamlError(null);
      return;
    }
    const timer = setTimeout(() => {
      const result = validateYaml(config.composeYaml);
      setYamlError(result.valid ? null : result.error || null);
    }, 500);
    return () => clearTimeout(timer);
  }, [config.composeYaml]);

  // ─── Validations ───────────────────────────────────────
  const isNameValid = useCallback((): boolean => {
    const name = config.name.trim();
    if (name.length < 3 || name.length > 40) return false;
    return /^[a-zA-Z0-9-]+$/.test(name);
  }, [config.name]);

  const isStepValid = (): boolean => {
    switch (step) {
      case 1:
        return isNameValid() && pricing.isReady;
      case 2: {
        const yamlResult = validateYaml(config.composeYaml);
        return yamlResult.valid && config.hostPort >= 1 && config.hostPort <= 65535 && config.healthPath.startsWith('/');
      }
      case 3:
        return confirmed;
      default:
        return false;
    }
  };

  // ─── Handlers ──────────────────────────────────────────
  const handleNext = () => {
    if (step < 3 && isStepValid()) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (!config.name || !pricing.pricingSelection) {
      showError(t('docker.validation.missingFields'));
      return;
    }

    setIsCreating(true);
    try {
      const selectedProduct = products.find((p: any) => p.category === 'DOCKER_HOST') || products[0];

      if (!selectedProduct || !selectedProduct.id) {
        showError(t('docker.validation.noProduct'));
        setIsCreating(false);
        return;
      }

      const instanceType = pricing.selectedVariant?.providerConfiguration?.instanceType ?? 't3.medium';
      const storage = pricing.selectedVariant?.includedResources?.storageGb ?? 30;

      const orderData: any = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'DOCKER',
          pricingSelection: pricing.pricingSelection,
          dockerConfig: {
            name: config.name.trim(),
            instanceType,
            storage,
            hostPort: config.hostPort,
            healthPath: config.healthPath,
            publicAccess: config.publicAccess,
            composeYaml: config.composeYaml,
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      success(t('docker.create.success'));
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error: any) {
      console.error('Error creating Docker Host order:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error creating order.';
      showError(errorMessage);
      setIsCreating(false);
    }
  };

  // ─── Step 1: Infrastructure ────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('docker.step1.title')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('docker.step1.subtitle')}
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('docker.fields.name')} *</label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          placeholder="my-docker-host"
          maxLength={40}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('docker.fields.nameHint')}
        </p>
        {config.name && !isNameValid() && (
          <p className="mt-1 text-xs text-red-500">
            {config.name.length < 3 ? t('docker.validation.nameMin') :
             config.name.length > 40 ? t('docker.validation.nameMax') :
             t('docker.validation.namePattern')}
          </p>
        )}
      </div>

      {/* Pricing plans */}
      <MarketplacePricingSelector
        basePlans={pricing.basePlans}
        variantsForSelected={pricing.variantsForSelected}
        selectedPlanCode={pricing.selectedPlanCode}
        selectedVariantCode={pricing.selectedVariantCode}
        addons={pricing.addons}
        selectedAddonCodes={pricing.selectedAddonCodes}
        isLoadingPlans={pricing.isLoadingPlans}
        isLoadingAddons={pricing.isLoadingAddons}
        onSelectPlan={pricing.setSelectedPlanCode}
        onSelectVariant={pricing.setSelectedVariantCode}
        onToggleAddon={pricing.toggleAddon}
      />

      {/* Public Access Toggle */}
      <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{t('docker.fields.publicAccess')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {t('docker.fields.publicAccessHint')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setConfig({ ...config, publicAccess: !config.publicAccess })}
          className={`relative w-11 h-6 rounded-full transition-colors ${config.publicAccess ? 'bg-black dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform shadow ${config.publicAccess ? 'translate-x-5 bg-white dark:bg-black' : 'translate-x-0 bg-white'}`} />
        </button>
      </div>

      {/* Region (fixed) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('docker.fields.region')}</label>
        <div className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
          US East (N. Virginia)
        </div>
      </div>
    </div>
  );

  // ─── Step 2: Docker Configuration ──────────────────────
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{t('docker.step2.title')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('docker.step2.subtitle')}
        </p>
      </div>

      {/* Compose YAML */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('docker.fields.composeYaml')} *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowExamples(!showExamples)}
              className="flex items-center gap-1 text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 font-medium"
            >
              {t('docker.step2.loadExample')} <DownOutlined className="text-xs" />
            </button>
            {showExamples && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                {Object.entries(COMPOSE_EXAMPLES).map(([key, ex]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setConfig({ ...config, composeYaml: ex.yaml });
                      setShowExamples(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {ex.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="relative border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="flex">
            {/* Line numbers */}
            <div className="select-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-right px-3 py-3 border-r border-gray-200 dark:border-gray-700 text-xs leading-[20px] font-mono">
              {(config.composeYaml || '\n').split('\n').map((_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>
            <textarea
              value={config.composeYaml}
              onChange={(e) => setConfig({ ...config, composeYaml: e.target.value })}
              placeholder={`services:\n  web:\n    image: nginx:alpine\n    ports:\n      - "80:80"\n    restart: unless-stopped`}
              spellCheck={false}
              className="flex-1 px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none resize-y text-[13px] leading-[20px]"
              style={{
                fontFamily: "'Fira Code', 'Monaco', 'Courier New', monospace",
                minHeight: '400px',
                tabSize: 2,
              }}
            />
          </div>
        </div>
        {yamlError && (
          <p className="mt-2 text-sm text-red-500 dark:text-red-400 font-mono bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            {yamlError}
          </p>
        )}
      </div>

      {/* Host Port */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('docker.fields.hostPort')} *</label>
        <input
          type="number"
          value={config.hostPort}
          onChange={(e) => setConfig({ ...config, hostPort: parseInt(e.target.value) || 0 })}
          min={1}
          max={65535}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('docker.fields.hostPortHint')}
        </p>
        {config.hostPort > 0 && (config.hostPort < 1 || config.hostPort > 65535) && (
          <p className="mt-1 text-xs text-red-500">{t('docker.validation.portRange')}</p>
        )}
      </div>

      {/* Health Path */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('docker.fields.healthPath')} *</label>
        <input
          type="text"
          value={config.healthPath}
          onChange={(e) => setConfig({ ...config, healthPath: e.target.value })}
          placeholder="/"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('docker.fields.healthPathHint')}
        </p>
        {config.healthPath && !config.healthPath.startsWith('/') && (
          <p className="mt-1 text-xs text-red-500">{t('docker.validation.healthPathSlash')}</p>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">{t('docker.step2.supportedImages')}</p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {t('docker.step2.supportedImagesDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Step 3: Review ────────────────────────────────────
  const renderStep3 = () => {
    const variant = pricing.selectedVariant;
    const inst = variant?.providerConfiguration?.instanceType ?? '—';
    const storageGb = variant?.includedResources?.storageGb;
    const totalStr = pricing.totalPrice
      ? `$${pricing.totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })} MXN`
      : '—';
    const composePreview = config.composeYaml.split('\n').slice(0, 20).join('\n');
    const totalLines = config.composeYaml.split('\n').length;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{t('docker.step3.title')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('docker.step3.subtitle')}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">{t('docker.review.infrastructure')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.name')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Plan</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {variant?.displayName ?? variant?.planName ?? '—'} ({variant?.variantName})
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.instanceType')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{inst}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.storage')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{storageGb ? `${storageGb} GB (gp3)` : '—'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.region')}</span>
              <span className="font-medium text-gray-900 dark:text-white">US East (N. Virginia)</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.publicAccess')}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {config.publicAccess ? t('docker.review.publicAccessYes') : t('docker.review.publicAccessNo')}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">{t('docker.review.dockerConfig')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.hostPort')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.hostPort}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('docker.fields.healthPath')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.healthPath}</span>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600 dark:text-gray-400 mb-2 block">{t('docker.review.composePreview')}</label>
            <pre className="text-xs bg-gray-100 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto font-mono text-gray-800 dark:text-gray-200 max-h-[300px] overflow-y-auto">
              {composePreview}
              {totalLines > 20 && `\n\n... (${totalLines - 20} more lines)`}
            </pre>
          </div>
        </div>

        {/* Cost */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-900 dark:text-white">{t('docker.review.estimatedCost') || 'Costo mensual'}</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalStr}<span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mes</span>
            </p>
          </div>
        </div>

        {/* Confirmation */}
        <div className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <input
            type="checkbox"
            id="confirm-checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-black focus:ring-black dark:focus:ring-white"
          />
          <label htmlFor="confirm-checkbox" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            {t('docker.step3.confirmation')}
          </label>
        </div>
      </div>
    );
  };

  // ─── Render Step ───────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  // ─── Main Render ───────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/marketplace?product=docker-host')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('docker.backToList')}</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('docker.create.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('docker.create.subtitle')}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: t('docker.steps.infrastructure') },
            { num: 2, label: t('docker.steps.dockerConfig') },
            { num: 3, label: t('docker.steps.review') },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step >= s.num
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div className={`h-1 flex-1 mx-2 rounded transition-all ${step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('docker.actions.back')}
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/marketplace?product=docker-host')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('docker.actions.cancel')}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('docker.actions.next')}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating || !confirmed}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <LoadingOutlined className="animate-spin" />
                  {t('docker.actions.creating')}
                </>
              ) : (
                t('docker.actions.create')
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateDockerHost;
